import React, { useState, useEffect } from "react";
import styled from "styled-components";
import Layout from "./layout";
import { campaignAPI } from "../services/api";

const PageContainer = styled.div`
  background-color: #ffffff;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
  padding: 2rem;
  margin-bottom: 2rem;
`;

const PageTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 400;
  color: #111827;
  margin-bottom: 2rem;
`;

const OrganizationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  margin-top: 2rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const OrganizationCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-4px);
    box-shadow:
      0 10px 15px -3px rgba(0, 0, 0, 0.1),
      0 4px 6px -2px rgba(0, 0, 0, 0.05);
    border-color: #00b5b8;
  }
`;

const LogoContainer = styled.div`
  width: 100%;
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  padding: 1rem;
  position: relative;
`;

const LogoPlaceholder = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #00b5b8 0%, #00979a 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 2rem;
  font-weight: 600;
  box-shadow: 0 4px 6px rgba(0, 181, 184, 0.15);
`;

const LogoImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const OrganizationName = styled.div`
  padding: 1.25rem 1.25rem 0.5rem 1.25rem;
  text-align: center;
  font-size: 1rem;
  font-weight: 500;
  color: #374151;
  background: #ffffff;
`;

const CampaignDetailLabel = styled.div`
  padding: 0 1.25rem 0.25rem 1.25rem;
  text-align: center;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  background: #ffffff;
`;

const CampaignDetailText = styled.div`
  padding: 0 1.25rem 1.25rem 1.25rem;
  text-align: center;
  font-size: 0.8rem;
  font-weight: 400;
  color: #374151;
  background: #ffffff;
  line-height: 1.4;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
`;

const LoadingSpinner = styled.div`
  border: 4px solid #f3f4f6;
  border-top: 4px solid #00b5b8;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.p`
  margin-top: 1rem;
  color: #6b7280;
  font-size: 0.875rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #6b7280;
`;

const DashboardOrganization = () => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadOrganizations = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const response = await campaignAPI.getList(1);

      if (response && response.result && response.list) {
        const FIREBASE_STORAGE_BASE_URL =
          "https://firebasestorage.googleapis.com/v0/b/diet-z.appspot.com/o/";

        const campaigns = response.list
          .filter(
            (campaign) =>
              campaign.name &&
              campaign.name.trim() !== "" &&
              campaign.name !== "-",
          )
          .map((campaign) => {
            let logoUrl = null;
            if (campaign.image && campaign.image.trim() !== "") {
              if (
                campaign.image.startsWith("http://") ||
                campaign.image.startsWith("https://")
              ) {
                logoUrl = campaign.image;
              } else {
                logoUrl = FIREBASE_STORAGE_BASE_URL + campaign.image;
              }
            }

            return {
              id: campaign.id,
              name: campaign.name,
              logo: logoUrl,
              caption: campaign.caption || "",
              hr_url: campaign.hr_url || null,
            };
          });

        setOrganizations(campaigns);
      } else {
        setOrganizations([]);
      }
    } catch (error) {
      console.error("Error loading campaigns:", error);
      setOrganizations([]);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    loadOrganizations();

    // refresh ทุก 120 วินาที เนื่องจาก hr_url มีอายุแค่ 120 วินาที
    const intervalId = setInterval(() => {
      loadOrganizations(false);
    }, 120 * 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  const handleOrganizationClick = (org) => {
    if (org.hr_url) {
      window.open(org.hr_url, "_blank");
    }
  };

  return (
    <Layout>
      <PageContainer>
        <PageTitle>Dashboard องค์กร</PageTitle>

        {loading ? (
          <LoadingContainer>
            <LoadingSpinner />
            <LoadingText>กำลังโหลด...</LoadingText>
          </LoadingContainer>
        ) : organizations.length === 0 ? (
          <EmptyState>ไม่พบข้อมูลองค์กร</EmptyState>
        ) : (
          <OrganizationGrid>
            {organizations.map((org) => (
              <OrganizationCard
                key={org.id}
                onClick={() => handleOrganizationClick(org)}
              >
                <LogoContainer>
                  {org.logo ? (
                    <LogoImage src={org.logo} alt={org.name} />
                  ) : (
                    <LogoPlaceholder>
                      {org.name.charAt(0).toUpperCase()}
                    </LogoPlaceholder>
                  )}
                </LogoContainer>
                <OrganizationName>{org.name}</OrganizationName>
                <CampaignDetailLabel>แคมเปญส่งเสริมสุขภาพ</CampaignDetailLabel>
                <CampaignDetailText>{org.caption || "-"}</CampaignDetailText>
              </OrganizationCard>
            ))}
          </OrganizationGrid>
        )}
      </PageContainer>
    </Layout>
  );
};

export default DashboardOrganization;
